import { useCallback, useRef } from 'react';
import { useJsApiLoader } from '@react-google-maps/api';

const LIBRARIES = ['places'];

// RAT-1825: validates a fully-typed Legal address against Google's geocoder so a
// real-time "this doesn't look like a known address" warning can be shown. Kept
// separate from useGooglePlacesAutocomplete - that hook is Autocomplete/PlacesService
// boilerplate for address *search*, this is Geocoder-based validation of an address
// already typed into the form. Shares the same loader `id` so the Maps script is
// still only ever loaded once, regardless of which hook mounts first.
export const useAddressGeocodeCheck = () => {
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey:
      process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'YOUR_API_KEY_HERE',
    libraries: LIBRARIES,
  });

  const geocoderRef = useRef(null);

  const checkAddressMatch = useCallback(
    (addressString) =>
      new Promise((resolve) => {
        if (!isLoaded || !window.google || !addressString) {
          resolve({ matched: true });
          return;
        }

        if (!geocoderRef.current) {
          geocoderRef.current = new window.google.maps.Geocoder();
        }

        geocoderRef.current.geocode(
          { address: addressString },
          (results, status) => {
            // Zero results, or Google explicitly flagging it couldn't line up every
            // component (partial_match), both read as "no known address matches this."
            const noMatch =
              status !== 'OK' ||
              !results?.length ||
              results[0]?.partial_match === true;
            resolve({ matched: !noMatch });
          },
        );
      }),
    [isLoaded],
  );

  return { isLoaded, checkAddressMatch };
};
